import { useMemo, useCallback, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Plus, Mail, Ban, CheckCircle } from 'lucide-react';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import {
  User,
  type AddOrgMemberRequest,
  type UpdateOrgMemberRequest,
  type AddOrgMemberResponse,
  type UpdateOrgMemberResponse,
  type DeleteOrgMemberResponse,
  type UserStatus
} from '@bliks-io/contracts';
import { useDepartmentMembers } from '@/api/queries/useDepartmentMembers';
import {
  useCreateOrgMember,
  useUpdateOrgMember,
  useDeleteOrgMember
} from '@/api/mutations/useOrgMembers';
import { useOrgMemberStatusActions } from '@/pages/organization/org-members/useOrgMemberStatusActions';
import { useCrudDialogState } from '@/hooks/useCrudDialogState';
import { useCrudHandlers } from '@/hooks/useCrudHandlers';
import {
  DeleteConfirmDialog,
  Badge,
  Button,
  Page,
  PageHeader,
  PageContent,
  UserCard,
  toUserCardProps,
  DataTable,
  createActionsColumn,
  createColumnHeader,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { OrgMemberFormDialog } from '@/pages/organization/org-members/components/OrgMemberFormDialog';
import { useAuthStore } from '@/stores/auth-store';
import { useManagedDepartmentSelection } from '@/hooks/useManagedDepartmentSelection';
import { ContentHeader } from '@/components/content-header';
import { DepartmentTabsNav } from '@/components/department/DepartmentTabsNav';
import { WorkEnvironmentTabsNav } from '@/components/work-environment/WorkEnvironmentTabsNav';
import {
  getUserStatusLabel,
  getUserStatusColorScheme,
  getUserStatusOptions
} from '@/lib/user-utils';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { ManagedDepartmentSelector } from '@/components/department/ManagedDepartmentSelector';

/**
 * Helper function to get status-specific actions for a member
 */
function getMemberStatusActions(
  member: User,
  handlers: {
    onSendInvitation: (memberId: string) => void;
    onSuspend: (memberId: string) => void;
    onUnsuspend: (memberId: string) => void;
  },
  t: (key: string) => string
) {
  const actions: Array<{
    label: string;
    icon: JSX.Element;
    onClick: () => void;
    variant?: 'destructive';
  }> = [];

  if (member.status === 'inactive') {
    actions.push({
      label: t('organization.members.sendInvitationAction'),
      icon: <Mail className="w-4 h-4" />,
      onClick: () => handlers.onSendInvitation(member._id)
    });
  } else if (member.status === 'pending') {
    // Pending users can have invitations resent or be suspended
    actions.push({
      label: t('organization.members.resendInvitationAction'),
      icon: <Mail className="w-4 h-4" />,
      onClick: () => handlers.onSendInvitation(member._id)
    });
    actions.push({
      label: t('organization.members.suspendUser'),
      icon: <Ban className="w-4 h-4" />,
      onClick: () => handlers.onSuspend(member._id),
      variant: 'destructive' as const
    });
  } else if (member.status === 'suspended') {
    actions.push({
      label: t('organization.members.unsuspendUser'),
      icon: <CheckCircle className="w-4 h-4" />,
      onClick: () => handlers.onUnsuspend(member._id)
    });
  } else if (member.status === 'active') {
    actions.push({
      label: t('organization.members.suspendUser'),
      icon: <Ban className="w-4 h-4" />,
      onClick: () => handlers.onSuspend(member._id),
      variant: 'destructive' as const
    });
  }

  return actions;
}

export function DepartmentMembersListPage() {
  const { t } = useTranslation();
  useDocumentTitle('pageTitles.departmentMembers');
  const slug = useAuthStore().slug();
  const navigate = useNavigate();
  const { dept: selectedDeptFromUrl } = useSearch({ from: '/$slug/department/members/' });
  const { managedDepartments, selectedDepartmentId, selectedDepartment, handleDepartmentChange } =
    useManagedDepartmentSelection('/$slug/department/members', selectedDeptFromUrl);

  const { data: departmentMembers, isLoading } = useDepartmentMembers({
    departmentId: selectedDepartmentId
  });

  // Mutations
  const createMutation = useCreateOrgMember();
  const updateMutation = useUpdateOrgMember();
  const deleteMutation = useDeleteOrgMember();

  // CRUD dialog state management
  const {
    formDialogOpen,
    editingEntity: editingMember,
    deleteDialogOpen,
    deletingEntityId: deletingMemberId,
    openCreate: handleCreate,
    openEdit: handleEdit,
    openDelete: handleDelete,
    closeFormDialog,
    closeFormDialogFrom,
    formDialogOpening,
    closeDeleteDialog
  } = useCrudDialogState<User>();

  // CRUD handlers with error handling
  const { handleFormSubmit, handleDeleteConfirm, isDeleting } = useCrudHandlers<
    User,
    AddOrgMemberRequest,
    UpdateOrgMemberRequest,
    AddOrgMemberResponse,
    UpdateOrgMemberResponse,
    DeleteOrgMemberResponse
  >({
    createMutation,
    updateMutation,
    deleteMutation,
    editingEntity: editingMember,
    deletingEntityId: deletingMemberId,
    t,
    onFormSuccess: closeFormDialog,
    onDeleteSuccess: closeDeleteDialog
  });

  // View handler
  const handleView = useCallback(
    (member: User) => {
      navigate({
        to: '/$slug/org-members/$orgMemberId',
        params: { slug, orgMemberId: member._id }
      });
    },
    [navigate, slug]
  );

  const {
    handleSendInvitation,
    handleSuspend,
    handleUnsuspend,
    pendingMemberIds,
    memberDialogProps
  } = useOrgMemberStatusActions({
    dialogMemberId: editingMember?._id,
    dialogOpening: formDialogOpening,
    onDialogActionDone: closeFormDialogFrom
  });

  // Define columns with stable reference using useMemo
  const columns = useMemo<DataTableColumnDef<User>[]>(
    () => [
      {
        id: 'name',
        accessorFn: (row) => `${row.firstName} ${row.lastName}`,
        meta: { label: t('common.Name'), filterType: 'text' },
        header: createColumnHeader(t('common.Name')),
        cell: ({ row }) => (
          <UserCard
            user={toUserCardProps(row.original)}
            variant="name-only"
            // The table has its own status column — never mark twice.
            showLifecycleSuffix={false}
          />
        ),
        enableColumnFilter: true,
        size: 280
      },
      {
        accessorKey: 'email',
        meta: { label: t('common.Email'), filterType: 'text' },
        header: createColumnHeader(t('common.Email')),
        cell: ({ row }) => row.getValue('email'),
        enableColumnFilter: true
      },
      {
        id: 'mainRole',
        accessorFn: (row) => row.mainRole?.name || '-',
        meta: { label: t('organization.members.mainRole'), filterType: 'text' },
        header: createColumnHeader(t('organization.members.mainRole')),
        cell: ({ row }) => row.original.mainRole?.name || '-',
        enableColumnFilter: true
      },
      {
        accessorKey: 'status',
        meta: {
          label: t('common.Status'),
          filterType: 'select',
          filterOptions: getUserStatusOptions(t)
        },
        header: createColumnHeader(t('common.Status')),
        cell: ({ row }) => {
          const status = row.getValue('status') as UserStatus;
          return (
            <Badge colorScheme={getUserStatusColorScheme(status)}>
              {getUserStatusLabel(status, t)}
            </Badge>
          );
        },
        enableColumnFilter: true
      },
      createActionsColumn<User>({
        onEdit: (member) => handleEdit(member),
        onDelete: (member) => handleDelete(member._id),
        onView: (member) => handleView(member),
        getCustomActions: (member) =>
          getMemberStatusActions(
            member,
            {
              onSendInvitation: handleSendInvitation,
              onSuspend: handleSuspend,
              onUnsuspend: handleUnsuspend
            },
            t
          ),
        isRowPending: (member) => pendingMemberIds.has(member._id)
      })
    ],
    [
      t,
      handleView,
      handleEdit,
      handleDelete,
      handleSendInvitation,
      handleSuspend,
      handleUnsuspend,
      pendingMemberIds
    ]
  );

  return (
    <Page>
      <PageHeader>
        <WorkEnvironmentTabsNav />
        <DepartmentTabsNav />
      </PageHeader>
      <PageContent>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <ContentHeader
              icon={ENTITY_ICONS.orgMember}
              title={t('department.members.title')}
              description={
                selectedDepartment
                  ? t('department.members.description', { department: selectedDepartment.name })
                  : t('department.members.descriptionDefault')
              }
            />
            <ManagedDepartmentSelector
              departments={managedDepartments}
              selectedDepartmentId={selectedDepartmentId}
              onDepartmentChange={handleDepartmentChange}
              className="w-[250px]"
            />
          </div>
          <Button variant="primary" size="medium" onClick={handleCreate}>
            <Plus className="w-4 h-4" />
            {t('department.members.createMember')}
          </Button>
        </div>

        <div className="mt-8">
          <DataTable
            columns={columns}
            data={departmentMembers || []}
            isLoading={isLoading}
            emptyMessage={t('department.members.empty')}
            enableGlobalFilter
            globalSearchPlaceholder={t('department.members.search', {
              defaultValue: 'Search members...'
            })}
            enableColumnVisibility
            enableColumnFilters
            enableUrlState
            tableId="departmentMembers"
          />
        </div>

        {/* Form Dialog */}
        <OrgMemberFormDialog
          open={formDialogOpen}
          onOpenChange={(open) => !open && closeFormDialog()}
          onSubmit={handleFormSubmit}
          member={editingMember}
          {...memberDialogProps}
          lockedDepartmentId={selectedDepartmentId}
          showUserTypes={false}
        />

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={(open) => !open && closeDeleteDialog()}
          onConfirm={handleDeleteConfirm}
          isDeleting={isDeleting}
          entityPhrase={t('organization.members.deletePhrase')}
        />
      </PageContent>
    </Page>
  );
}
